import { useI18n } from '@ecom/i18n';
import {
  Card,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { formatCount, formatTnd } from '../format';
import { type DashboardKpis } from '../types';

/** Top merchants by revenue (cancelled orders excluded), with the administrator who manages each. */
export function TopMerchantsTable({
  merchants,
}: {
  readonly merchants: DashboardKpis['topMerchants'];
}): ReactElement {
  const { t, locale } = useI18n();
  if (merchants.length === 0) return <EmptyState title={t('admin.dashboard.top.none')} />;
  return (
    <div data-testid="dashboard-top-merchants">
      <Table caption={t('admin.dashboard.section.topMerchants')}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.dashboard.top.colMerchant')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.top.colRevenue')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.top.colOrders')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.dashboard.top.colManager')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {merchants.map((merchant) => (
            <TableRow key={merchant.tenantId}>
              <TableCell>{merchant.name ?? t('admin.dashboard.act.unnamed')}</TableCell>
              <TableCell align="end">{formatTnd(merchant.revenue, locale)}</TableCell>
              <TableCell align="end">{formatCount(merchant.orders, locale)}</TableCell>
              <TableCell>{merchant.managerEmail ?? t('admin.dashboard.act.noManager')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** The five products sold in the greatest quantity (cancelled orders excluded), with their merchant. */
export function TopProductsTable({
  products,
}: {
  readonly products: DashboardKpis['products']['topSold'];
}): ReactElement {
  const { t, locale } = useI18n();
  if (products.length === 0) return <EmptyState title={t('admin.dashboard.pro.none')} />;
  return (
    <div data-testid="dashboard-top-products">
      <Table caption={t('admin.dashboard.pro.topTitle')} captionVisible>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.dashboard.pro.colProduct')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.dashboard.pro.colMerchant')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.pro.colQuantity')}</TableHeaderCell>
            <TableHeaderCell align="end">{t('admin.dashboard.pro.colRevenue')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => (
            <TableRow key={`${product.tenantId}:${product.productId}`}>
              <TableCell>{product.name}</TableCell>
              <TableCell>{product.merchantName ?? t('admin.dashboard.act.unnamed')}</TableCell>
              <TableCell align="end">{formatCount(product.quantity, locale)}</TableCell>
              <TableCell align="end">{formatTnd(product.revenue, locale)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** The latest shops handed from one administrator to another (the portfolios screen holds the full picture). */
export function RecentReassignments({
  latest,
}: {
  readonly latest: DashboardKpis['security']['recentReassignments']['latest'];
}): ReactElement {
  const { t, locale } = useI18n();
  return (
    <Card className="flex min-w-0 flex-col gap-2 p-4" data-testid="dashboard-recent-reassignments">
      <h3 className="font-display text-sm font-bold text-ink">
        {t('admin.dashboard.act.recentTitle')}
      </h3>
      {latest.length === 0 ? (
        <p className="text-sm text-inkFaint">{t('admin.dashboard.act.recentNone')}</p>
      ) : (
        <ul className="flex flex-col gap-1.5 text-sm text-ink">
          {latest.map((item) => (
            <li
              key={`${item.tenantId}:${item.at}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3"
            >
              <span dir="auto" className="min-w-0 [overflow-wrap:anywhere]">
                {t('admin.dashboard.act.recentRow', {
                  name: item.name ?? t('admin.dashboard.act.unnamed'),
                  admin: item.toAdminEmail,
                })}
              </span>
              <time className="text-xs text-inkFaint" dateTime={item.at}>
                {new Intl.DateTimeFormat(
                  locale === 'ar' ? 'ar-TN' : locale === 'fr' ? 'fr-TN' : 'en-TN',
                  { dateStyle: 'medium', timeStyle: 'short' },
                ).format(new Date(item.at))}
              </time>
            </li>
          ))}
        </ul>
      )}
      <Link to="/portfolios" className="text-xs font-medium text-accent hover:underline">
        {t('admin.dashboard.act.openPortfolios')}
      </Link>
    </Card>
  );
}
